/**
 * Vocabulary Service
 *
 * Fetches organization vocabulary from the API and transforms it
 * into the format expected by the MatchingEngine.
 */

import type { CustomSynonyms } from './matching-engine';

interface VocabularyTerm {
  id: string;
  canonicalTerm: string;
  variant: string;
  confidence: number;
}

interface VocabularyResponse {
  vocabulary: VocabularyTerm[];
  total: number;
}

/**
 * Fetch organization vocabulary and transform to CustomSynonyms format.
 * Returns empty object if no org context or fetch fails.
 */
export async function fetchOrgVocabulary(
  authToken: string | null | undefined,
  orgId: string | null | undefined
): Promise<CustomSynonyms> {
  if (!authToken || !orgId) {
    return {};
  }

  try {
    const response = await fetch(`/api/organizations/${orgId}/vocabulary`, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      // Silently fail - org vocab is an enhancement, not required
      console.warn('Failed to fetch org vocabulary:', response.status);
      return {};
    }

    const data: VocabularyResponse = await response.json();

    // Transform to CustomSynonyms format: { canonical: [variants...] }
    const synonyms: CustomSynonyms = {};

    for (const term of data.vocabulary) {
      const canonical = term.canonicalTerm.toLowerCase();
      const variant = term.variant.toLowerCase();

      if (!synonyms[canonical]) {
        synonyms[canonical] = [];
      }

      // Add variant if not already present
      if (!synonyms[canonical].includes(variant)) {
        synonyms[canonical].push(variant);
      }
    }

    if (Object.keys(synonyms).length > 0) {
      console.log(`📚 Loaded ${Object.keys(synonyms).length} org vocabulary terms`);
    }

    return synonyms;
  } catch (error) {
    console.warn('Error fetching org vocabulary:', error);
    return {};
  }
}
